/* eslint-disable max-lines */
import type { ReactElement } from "react"

import type { ConfigFairUse, MyUsage } from "./fair-use"

export const enum RetrievalMode {
  Hybrid = "hybrid",
  Vectors = "vectors",
  Text = "text",
}

export const enum GPT4VInput {
  TextAndImages = "textAndImages",
  Images = "images",
  Texts = "texts",
}

export const enum VectorFieldOptions {
  Embedding = "embedding",
}

export interface DisclaimerItem {
  type: "info" | "warn"
  translation_key: string
  allow_do_not_show_again?: boolean
}

export interface GPTModelItem {
  display_name: string
  id: string
  type: string
  default?: boolean
  disclaimers?: DisclaimerItem[]
  requiredModules?: string[]
  blockedModules?: string[]
  input_token_limit: number
  description: string
  feature_flags: {
    supports_images: boolean
    supports_streaming: boolean
    supports_temperature: boolean
    is_reasoning_model: boolean
    supports_thinking_streaming: boolean
    supports_system_message: boolean
    supports_max_completion_tokens: boolean
    supports_reasoning_efforts: boolean
    supports_tool_calls: boolean
  }
  /** Selector grouping: "current" (GPT-5.6 + Auto) shown directly, "legacy"
   *  (everything below 5.6) under the Legacy submenu. Derived server-side in
   *  `app/backend/services/model_capabilities.py`. Optional for backward compat
   *  with configs served before this field shipped. */
  tier?: "current" | "legacy"
  /** Ordered reasoning-effort stops this model supports (subset of
   *  none/minimal/low/medium/high/xhigh/max). Empty ⇒ non-reasoning ⇒ no slider. */
  supported_efforts?: string[]
  /** The effort the slider preselects for this model (e.g. "medium"; "" when none). */
  default_effort?: string
}

export type ChatAppRequestOverrides = {
  retrieval_mode?: RetrievalMode
  semantic_ranker?: boolean
  semantic_captions?: boolean
  exclude_category?: string
  /** When sent (showCategoryPicker + restrictToLibraryId), empty array = uncategorized chunks only; non-empty = OR match. Omit when picker is not shown (e.g. multi-library restrictToLibraryIds). */
  include_categories?: string[]
  top?: number
  temperature?: number
  prompt_template?: string
  target_language?: string
  use_translator?: boolean
  target_languages?: string[]
  translation_source_kind?: "text" | "files" | "documents"
  translation_session_files?: string[]
  translation_dictionaries?: Record<string, Record<string, string>>
  custom_translation_instructions?: string
  custom_system_prompt?: string
  translation_dictionary?: { [key: string]: string }
  prompt_template_prefix?: string
  prompt_template_suffix?: string
  prompt_id?: string
  prompt_title?: string
  suggest_followup_questions?: boolean
  use_oid_security_filter?: boolean
  use_groups_security_filter?: boolean
  use_gpt4v?: boolean
  use_custom_gpt?: boolean
  gpt4v_input?: GPT4VInput
  custom_gpt_model?: string
  vector_fields: VectorFieldOptions[]
  session_id?: string
  route_id?: string
  use_web_search?: boolean
  use_m365?: boolean
  /** Per-turn personal-search switch. Only an explicit `false` is ever sent: the server
   *  combines it with the stored per-chat choice (effective = chat AND turn), and an absent
   *  field means on. */
  use_personal_search?: boolean
  use_service_desk?: boolean
  use_netbox?: boolean
  use_x_one?: boolean
  /** Per-chat opt-out for the plugin-store meta-tools (plugin_catalog /
   *  plugin_operations / plugin_execute). Absent means on: the backend keeps
   *  attaching them whenever the tenant enabled the store, so API callers and
   *  older clients are unaffected. */
  use_plugins?: boolean
  /** Explicit per-chat allowlist of installed plugin ids. Absent means every
   *  installed plugin, an empty list means none. It only ever narrows: the
   *  backend still authorizes installation, distribution and each operation. */
  plugin_ids?: string[]
  use_agent_mode?: boolean
  demo_mode?: boolean
  search_services?: string[]
  is_continuation?: boolean
  reasoning_effort?: string
  /** Per-session opt-in for the `library_tree_search` agent tool. When
   *  `true`, the backend exposes the hierarchical tree-walk retrieval
   *  tool to the explorer agent; when missing or `false` the tool is
   *  stripped from the tool list. The admin `tree_retrieval` feature
   *  flag is a separate, higher-priority gate. Default: `false` —
   *  non-UI API callers must explicitly opt in. */
  enable_tree_retrieval?: boolean
  active_citations?: string[]
  start_research?: boolean
  report_length?: "short" | "standard" | "extended" | "custom"
  research_depth?: "dev" | "quick" | "detailed" | "comprehensive"
  language?: string
  report_language?: string
  report_format?: string | { filename: string; content: number[] }
  report_format_source?: "file" | "message" | "standard" | "structured_summary"
  sources?: ("web" | "company_libraries" | "user_documents")[]
  country_codes?: string[]
  source_urls?: string[]
  trusted_mode?: "only" | "preferred"
  file_handling_strategy?: "search_in_files" | "use_files_as_context"
  custom_report_length?: number
  workflow_id?: string
  document_ids?: string[]
  active_document_id?: string
  active_document_can_edit?: boolean
  active_document_title?: string
  active_document_content_snippet?: string
  active_selection_text?: string
  active_selection_start?: number
  active_selection_end?: number
  document_task_mode?: "edit" | "explain"
  document_task_instruction?: string
  document_task_scope?: "preview_only" | "persistent"
  // Indicates session has files indexed in localdocs for this chat
  has_indexed_files?: boolean
  // Additional session IDs to include (e.g., project aggregator sessions)
  extra_session_ids?: string[]
  // Agent profile name (e.g., "explorer", "workflow_builder")
  agent_name?: string
  /** Dash-only canonical live spec used to ground surgical chat edits. */
  dash_current_spec?: Record<string, unknown>
  // Style template editor context
  style_template_title?: string
  style_template_description?: string
  style_template_prompt_fragment?: string
  style_template_brand_colors?: string[]
  // Tender case context — enables doc_content_reader to access tender blob storage
  tender_case_id?: string
}

export type ResponseMessage = {
  content: string
  role: string
  /**
   * Files this assistant turn delivered as attachments, carried so a hosted
   * agent's next turn knows what it already produced. Names only: the content
   * is reachable as a session source, and re-sending it would put a whole
   * report back into every later prompt. Projected into the agent's history by
   * `services/hermes/hermes/agent_loop.py::_history_messages`.
   */
  assistant_files?: string[]
}

export type Thoughts =
  | {
      title: string
      description: string // It can be any output from the api
      props?: { [key: string]: string }
    }
  | null
  | undefined

export type ResponseContextDataPoint = {
  text: string[]
  images?: { url: string }[]
  title?: string
}

export type ResponseContextDataPoints = {
  text: string[]
  images?: { url: string }[]
  web_content?: unknown[]
  library_content?: unknown[]
  m365_content?: unknown[]
}

export type QueryCaptionResult = {
  additional_properties: { [key: string]: string | number | boolean }
  text: string
  highlights: string[]
}

export type PendingFile = { file_id: string; filename: string }

export type ToolApproval = {
  request_id: string
  call_id: string
  tool: string
  title: string
  summary: string
  arguments: Record<string, unknown>
  status: "pending" | "approved" | "denied" | "expired" | "cancelled"
  created_at: string
  updated_at: string
  decision_user?: string | null
  reason?: string | null
  timeout_secs?: number | null
  /** Set when the approval is asked only because personal content is part of the chat. */
  gate?: "personal_sources"
  /** See `DurableChatTurnPendingControl.argumentsElided` -- only ever set on
   *  a durable turn's mapped control; the legacy WebSocket transport always
   *  streams full arguments and never sets this. */
  argumentsElided?: boolean
}

export type ExtraDataValue =
  | string
  | number
  | boolean
  | ExtraDataValue[]
  | { [key: string]: ExtraDataValue }

export type Document = {
  id: string | null
  content: string | null
  embedding: number[] | null
  category: string | null
  sourcepage: string | null
  sourcefile: string | null
  oids: string[] | null
  groups: string[] | null
  block_oids?: string[] | null
  block_groups?: string[] | null
  unrestricted?: boolean | null
  captions: QueryCaptionResult[]
  search_score?: number
  search_rerank_score?: number
  extra_data?: { [key: string]: ExtraDataValue }
  /** Manifest/source basename when it differs from indexed `sourcefile` (e.g. external v2 conversion). */
  original_sourcefile?: string | null
}

export type UserDocumentParticipantRole = "owner" | "admin" | "editor" | "reader"

export type UserDocumentParticipant = {
  oid: string
  displayName: string
  type: "user" | "group"
  role: UserDocumentParticipantRole
}

export type DocumentVersion = {
  version: number
  content: string
  timestamp: string
  edit_source?: "user" | "ai" | "system"
  edit_preview?: string
  editor?: {
    oid?: string
    displayName?: string
  }
}

export type UserDocument = {
  id: string
  ownerUserId: string
  title: string
  content: string
  contentFormat?: string
  created?: string
  updated?: string
  projectId?: string | null
  sourceSessionId?: string | null
  sourceMessageId?: string | null
  participants?: UserDocumentParticipant[]
  newParticipantNotifications?: Array<{
    userId: string
    invitedBy?: string
    invitedByDisplayName?: string
    role?: UserDocumentParticipantRole
    invitedAt?: string
    viewedAt?: string
  }>
  versions?: DocumentVersion[]
  currentUserRole?: UserDocumentParticipantRole
  artifactType?: string
  artifactMeta?: Record<string, unknown>
}

export type DocumentReferencePreview = {
  document_id: string
  title: string
  snippet: string
  change_type?: "created" | "updated" | "edited" | "read" | "searched"
  version?: number
  updated_at?: string
}

export interface ScholarState {
  messages?: unknown[]
  action?: string
  latest_plan?: string
  initial_query?: string
  research_preferences?: Record<string, unknown>
  enriched_context?: {
    files_analysis?: string
    web_search_results?: string
    [key: string]: unknown
  }
  next_node?: string
  analyzed_files_cache?: Record<string, string>
  extracted_texts_cache?: Record<string, Record<string, unknown>[]>
}

/**
 * Deep activity event shape persisted on assistant messages for Deep Explorer.
 * Matches the live `DeepActivityEvent` type in `@/stores/ai-explorer-store`
 * (defined structurally here to avoid a store→models dependency cycle).
 * A persisted list may also contain a `{ event: "truncated", dropped: N }`
 * sentinel when the backend dropped events to stay under the Cosmos ceiling.
 *
 * Status events (`event: "status"`) are emitted by `_make_status_event` in
 * `app/backend/approaches/explorerdeep/streaming.py`. Their semantic fields
 * (`label`, `level`, `translation_key`, `artifact_id`) live inside
 * `result_summary` on the wire, but defensive top-level accessors are
 * declared here so older persisted rows or future BE shape changes don't
 * silently drop information at hydration. (#C3)
 */
export type PersistedDeepActivityEvent = {
  id: string
  event: "tool_start" | "tool_end" | "truncated" | "status"
  tool?: string
  source?: string
  dispatch_id?: string | null
  timestamp?: string
  input_summary?: Record<string, unknown>
  result_summary?: Record<string, unknown>
  dropped?: number
  // Status-event optional fields. Present at top level only on legacy or
  // forward-shaped persisted rows; the canonical wire shape has them inside
  // `result_summary`. The hydration loop reads either site.
  label?: string
  level?: string
  translation_key?: string
  artifact_id?: string
}

export type ResponseContext = {
  data_points: string[] | ResponseContextDataPoints | ResponseContextDataPoint[]
  followup_questions: string[] | null
  thoughts: Thoughts[]
  documents?: [string, Document][]
  document_references?: DocumentReferencePreview[]
  company_name?: string
  attachment?: string
  agent_state?: ScholarState
  session_id?: string
  message_id?: string
  fact_check_result?: Record<string, unknown>
  workflow_run?: ChatAppResponse["workflow_run"]
  tool_approvals?: ToolApproval[]
  /**
   * Marks a message built from the user's personal sources (mail, OneDrive, chats). Content-free:
   * the sources used. The renderer neutralises external links and images in a marked message, and
   * the server never sends a marked message to anyone but its owner.
   */
  personal_sources?: { version: number; sources?: string[]; redacted?: boolean }
  pending_files?: PendingFile[]
  file_creation_failed?: boolean
  /**
   * Files from earlier turns of this session that the answer links to again.
   * They resolve sandbox download links but never render as file cards.
   */
  referenced_files?: string[]
  /**
   * Rich deep_activity_event payloads collected during a Deep Explorer turn.
   * Used to rehydrate the side-panel activity log and sources ribbon when the
   * user reopens an old chat. Backend trims and caps this list — see
   * `app/backend/approaches/explorerdeep/activity_persistence.py`.
   */
  deep_activity_events?: PersistedDeepActivityEvent[]
  /**
   * Workspace artifacts emitted during this assistant turn. Persisted by the
   * backend artifact accumulator; rehydrated by the artifact store slice on
   * chat reload. See `.workspace-spec/WORKSPACE_CONTRACT.md` (Persistence
   * schema). Backend trims and caps this list per the spec.
   */
  artifacts?: Artifact[]
  /**
   * Id of the Canvas document this Deep Explorer session was most recently
   * saved into. Used to drive "Save to Canvas" vs "Update Canvas" behaviour
   * — later clicks call `updateDocumentApi` instead of creating a duplicate.
   */
  canvas_document_id?: string | null
  presentation_decks?: Array<{
    filename: string
    pdf_filename?: string
    preview_images: string[]
    slide_count: number
  }>
  /**
   * HERMES B4: set by the hosted agent's `propose_artifact` tool during the
   * turn (server-reserved context key — survives context replaces). The chat
   * view renders it as an inline "publish this?" card; nothing is published
   * until the user confirms.
   */
  artifact_candidate?: {
    title: string
    kind: "markdown" | "text" | "document"
    summary: string
  }
}

// Legacy format types for backward compatibility
export type ResponseChoice = {
  index: number
  message: ResponseMessage
  context: ResponseContext
  session_state: string | null
}

export type LegacyApiResponse = {
  choices: ResponseChoice[]
  completed: boolean
  animated: boolean
  error?: string
  activities?: ActivityMessage[]
}

export type ChatAppResponseOrError = {
  content?: string
  context?: ResponseContext
  session_state?: string | null
  error?: string
  error_id?: string
  /** Stable machine-readable id for `error` — see `ChatAppResponse.error_code`. */
  error_code?: string
  completed?: boolean
  // Optional files attached to this message (assistant or user)
  assistant_files?: string[]
  user_files?: string[]
  edited_via_canvas?: boolean
  // Optional i18n hint persisted alongside `error` (see ErrorMessage) so a
  // reloaded/historical session can still render a localized message.
  translation_key?: string
  translation_params?: Record<string, unknown>
}

/** Represents a tool call made during a voice exchange */
export type VoiceToolCall = {
  name: string
  callId: string
  arguments?: Record<string, unknown>
  result?: Record<string, unknown>
  success: boolean
  assistantFiles?: string[]
  timestamp?: number
}

/** Represents an activity event during a voice exchange */
export type VoiceActivityItem = {
  activityType: string
  message: string
  details?: Record<string, unknown>
  timestamp?: number
}

/** Represents a single user→assistant exchange in a voice conversation */
export type VoiceTranscriptTurn = {
  userText: string
  assistantText: string
  toolCalls?: VoiceToolCall[]
  activities?: VoiceActivityItem[]
  assistantFiles?: string[]
  timestamp?: number
}

export type ChatAppResponse = {
  content: string
  /**
   * Client-side only: unique id stamped at stream start. The finalize settle
   * uses it to prove it still owns the streaming slot — question text is not
   * a safe identity when a fast follow-up repeats the same text (#2426).
   */
  turn_id?: string
  /**
   * Client-side only: `Date.now()` when the stream slot was created. Used to
   * drop late WebSocket activity frames from a previous turn after queue drain
   * / fast follow-up starts the next send (#2437).
   */
  stream_started_at?: number
  /** Deep Explorer: pre-approval reasoning prose shown in a collapsible "Thinking" panel. */
  thinking?: string
  context: ResponseContext
  session_state: string | null
  completed: boolean
  /** Steer/cancel (issue #2438): true when generation was cooperatively cut
   *  short by a "stop & redirect" cancel. The message is still `completed`
   *  (terminal, persisted) — this is the "marked interrupted" signal for the
   *  "Response stopped" marker, distinct from `completed:false`'s in-flight
   *  meaning elsewhere in the codebase. */
  interrupted?: boolean
  /** Present alongside `interrupted`. Legacy always stamps `"cancelled"`
   *  (see `approach.py`'s `response_dict["termination_reason"] = "cancelled"`
   *  — it never emits any other value). The durable-turn state machine
   *  (`durable-chat-turn-state.ts`) additionally emits `"interrupted"` for
   *  process-loss termination — safe to branch on that exact string without
   *  a `durable_turn` guard, since legacy can never produce it. */
  termination_reason?: string
  /**
   * Client-side only: stamped by `buildChatAppResponseFromSnapshot` in
   * `durable-chat-turn-state.ts` on every answer the durable-turn state
   * machine constructs. Legacy never sets this. It exists because a durable
   * terminal `cancelled`/`interrupted` answer can otherwise take the exact
   * same shape as a legacy one (empty content, empty activities,
   * `interrupted: true`, `termination_reason: "cancelled"`) — this flag lets
   * shared renderers (`system-message.tsx`) scope durable-only UI fixes
   * without changing legacy's pixel-for-pixel behavior (issue #2513).
   */
  durable_turn?: boolean
  /**
   * Prism capture outcomes that may accompany a SAVED answer: a stopped
   * durable partial, a completed durable answer whose source lineage was
   * incomplete or whose capture is still pending (from the turn's terminal
   * status), or a legacy answer (from the `message_saved` ack). Kept separate
   * from `error` so the answer and its neutral notice stay visible (#3339).
   * Never means "stored in Prism".
   */
  memory_capture_status?:
    | "memory_capture_blocked_source"
    | "memory_capture_unconfirmed"
    | "memory_capture_pending"
  error?: string
  error_id?: string
  /**
   * Stable machine-readable id for `error`, when the producer had one.
   *
   * Durable failed turns stamp it from `DurableChatTurnError.code`
   * (`buildChatAppResponseFromSnapshot`); the legacy WS handler stamps the
   * matching session `errorCode`. Renderers switch on this instead of matching
   * the free-text `error`, which is backend prose and may be reworded:
   * `system-message.tsx` and the session-error `Chat.Error` card use it to
   * show the fair-use gate rather than the generic error card for
   * `"fair_use_blocked"` (#2625).
   */
  error_code?: string
  // Optional i18n hint persisted alongside `error` — see ErrorMessage /
  // ChatAppResponseOrError.
  translation_key?: string
  translation_params?: Record<string, unknown>
  activities?: ActivityMessage[]
  assistant_files?: string[]
  user_files?: string[]
  edited_via_canvas?: boolean
  canvas_versions?: Array<{
    version: number
    content: string
    timestamp: string
    is_original?: boolean
    edit_description?: string
  }>
  messageId?: string // Added for Canvas editing
  voiceTranscriptTurns?: VoiceTranscriptTurn[] // Voice conversation turns from realtime audio (legacy)
  isVoiceMessage?: boolean // Indicates this message originated from voice mode
  workflow_run?: {
    id: string
    workflow_id: string
    workflow_title?: string | null
    status: "queued" | "running" | "paused" | "succeeded" | "failed"
    trigger?: "manual" | "schedule" | "webhook" | "document_indexed"
    output?: string
    error?: string | null
    resumable?: boolean
    pending_interrupt?: {
      type?: string
      node_id?: string
      input_id?: string
      prompt?: string
      preview?: string
    } | null
  }
}

export type ChatAppQuestion = {
  message: string
  images: File[]
}

export type ChatAppRequestContext = {
  overrides?: ChatAppRequestOverrides
}

export type ChatAppRequest = {
  /** Stable client-generated identity used for idempotent durable turn acceptance. */
  turn_id?: string
  messages: ResponseMessage[]
  context?: ChatAppRequestContext
  stream?: boolean
  session_state: string | null
  client_capabilities?: {
    deep_ask_user_response_ids?: boolean
  }
}

/**
 * Durable-turn HTTP DTOs use camelCase. `status` is the canonical turn status;
 * `lifecycleState` is reserved for separate session-lifecycle metadata.
 */
export const DURABLE_CHAT_TURN_STATUSES = [
  "accepted",
  "running",
  "waiting-approval",
  "completed",
  "failed",
  "cancelled",
  "interrupted",
] as const

export type DurableChatTurnStatus = (typeof DURABLE_CHAT_TURN_STATUSES)[number]

export const DURABLE_CHAT_TURN_MODES = ["explorer", "agent", "scholar", "translate"] as const

export type DurableChatTurnMode = (typeof DURABLE_CHAT_TURN_MODES)[number]

export type DurableChatTurnIdentity = {
  sessionId: string
  turnId: string
  epoch: number
}

export type DurableChatTurnError = {
  message: string
  code?: string
  errorId?: string
  /**
   * Fair-use snapshot carried by a `code: "fair_use_blocked"` terminal
   * (#2625): the same `QuotaSnapshot.to_dict()` subset the legacy WS error
   * frame puts on `data.usage` and `GET /api/me/usage` returns in full.
   *
   * `Partial` on purpose — it is a refusal payload, not the authoritative
   * snapshot, so an older backend (or a trimmed payload) may carry only some
   * of the fields. `extractFairUseError` (features/fair-use/fair-use-utils.ts)
   * validates every field's type before any of it reaches the cache, so this
   * type only has to be permissive enough not to lie about what is guaranteed.
   */
  usage?: Partial<MyUsage>
}

export type DurableChatTurnPendingControl = {
  action: "tool_decision"
  requestId: string
  callId: string
  tool?: string
  arguments?: Record<string, unknown>
  /** True when the backend dropped `arguments` from this event to fit its
   *  persisted-event size budget. The tool still executes with its full,
   *  original arguments after approval -- this only affects what's shown
   *  here, so a consumer MUST treat a missing `arguments` as "hidden, not
   *  empty" and refuse to let the user approve blind. */
  argumentsElided?: boolean
}

/**
 * Post-enrichment parent-session fields (issue #2513) — the durable
 * equivalent of the legacy `title_state_changed` WebSocket frame's payload
 * plus the chat-list metadata a session-history refetch would deliver.
 * Mirrors `_session_delivery_fields` in `services/chat_turn_enrichment.py`
 * exactly. Carried whole-value (never merged field-by-field) on the durable
 * snapshot whenever a turn's completion enriched its session — legacy
 * `Approach.save_message` does this synchronously; the durable path bypasses
 * `save_message` entirely, so without this the chat is never named and never
 * surfaces correctly in the chat list.
 *
 * Every field beyond `sessionId` is best-effort: enrichment is guarded and
 * time-bounded on the backend, so a slow/broken step can leave any of them
 * absent or `null`. `title` is always present (possibly `""` if the backend
 * hasn't generated one yet) — frontend consumers must never let an empty
 * `title` blank an already-rendered one.
 */
export type DurableChatTurnEnrichedSession = {
  sessionId: string
  title: string
  pendingTitleSuggestion?: NonNullable<ChatSessionModel>["pending_title_suggestion"] | null
  messageCount?: number
  iconType?: NonNullable<ChatSessionModel>["iconType"] | null
  lastFunctionUsed?: NonNullable<ChatSessionModel>["lastFunctionUsed"] | null
}

/** Wire statuses a durable steer note can settle to. Deliberately excludes the
 *  client-only `pending`: the server never reports a note as pending, it
 *  simply has not reported on it yet. */
export type DurableSteerNoteOutcome = "delivered" | "undelivered" | "rejected"

export type DurableChatTurnSnapshot = DurableChatTurnIdentity & {
  status: DurableChatTurnStatus
  snapshotSeq: number
  content: string
  context: ResponseContext
  activities: ActivityMessage[]
  responseComplete: boolean
  controls: DurableChatTurnPendingControl[]
  finalMessageId?: string
  error?: DurableChatTurnError
  session?: DurableChatTurnEnrichedSession
  /**
   * Per-note outcomes for durable steer notes (#2814), keyed by the client's
   * own `steer_id` — the same identity the WebSocket `steer_status` frame
   * settles a composer chip by, so one reducer serves both transports.
   *
   * Accumulated from live `steer_status` events only; the backend does not
   * carry it in the snapshot body. A client that misses the event (a replay
   * gap large enough to force a snapshot replace) leaves that chip `pending`,
   * which is the honest reading — it genuinely does not know the outcome.
   * Absent on any snapshot that has seen no statuses.
   */
  steerStatuses?: Record<string, DurableSteerNoteOutcome>
  /**
   * Set (only when true) by `build_snapshot_dto` / `_snapshot_frame_body` /
   * `_terminal_frame` when the backend's `_durable_snapshot_body`
   * (chat_turn_runner.py) had to drop `content` from this snapshot entirely
   * because the answer alone busted the live snapshot's byte budget — never a
   * truncated string. `content` above is then empty/stale and MUST NOT be
   * treated as authoritative: `durable-chat-turn-registry.ts`'s
   * `replaceFromSnapshot` reads this to keep whatever content a reconnecting
   * client already has instead of clobbering it with this DTO's content.
   */
  contentOmitted?: boolean
}

export type DurableChatTurnEvent = DurableChatTurnIdentity & {
  type: "turn_event"
  seq: number
  eventType: string
  data: unknown
}

export type DurableChatTurnReplayGap = DurableChatTurnIdentity & {
  type: "replay_gap"
  requestedAfterSeq: number
  firstAvailableSeq: number
  snapshotSeq: number
}

export type DurableChatTurnAcceptedMessage = DurableChatTurnIdentity & {
  type: "turn_accepted"
  status: DurableChatTurnStatus
  lastSeq: number
}

export type DurableChatTurnSnapshotMessage = {
  type: "turn_snapshot"
  snapshot: DurableChatTurnSnapshot
}

export type DurableChatTurnTerminalStatus = Extract<
  DurableChatTurnStatus,
  "completed" | "failed" | "cancelled" | "interrupted"
>

export type DurableChatTurnTerminalMessage = DurableChatTurnIdentity & {
  type: "turn_terminal"
  seq: number
  status: DurableChatTurnTerminalStatus
  snapshot: DurableChatTurnSnapshot
}

export type DurableChatTurnSubscriptionMessage =
  | DurableChatTurnAcceptedMessage
  | DurableChatTurnEvent
  | DurableChatTurnReplayGap
  | DurableChatTurnSnapshotMessage
  | DurableChatTurnTerminalMessage

export type DurableChatTurnAcceptRequest = {
  turnId: string
  mode: DurableChatTurnMode
  request: ChatAppRequest
}

type DurableChatTurnDetailsResponse = {
  sessionId: string
  turnId: string
  epoch: number
  status: DurableChatTurnStatus
  lastSeq: number
  snapshot: DurableChatTurnSnapshot
  leaseExpiresAt?: string
  finalMessageId?: string
  cancelRequestedAt?: string
  cancelReason?: string
  /** The turn's durable mode, echoed back by the backend (`build_details_dto`
   *  in chat_turn_service.py) because the frontend hydration depends on it. */
  mode?: DurableChatTurnMode
  /** Best-effort echo of the originating question text — see
   *  `build_details_dto`'s docstring. Absent (not merely `""`) means the
   *  echo is unavailable (e.g. the process-local echo registry was recycled
   *  after a restart), in which case hydration falls back to `""` rather
   *  than mislabeling the turn with an unrelated question. */
  question?: string
  /**
   * Non-null only when `snapshot` is a genuine, fully-reconstructed covering
   * snapshot the backend actually read (mirrors `DurableChatTurnStatusResponse
   * .replayGap`, which `get_status` always sets). Neither `accept` nor
   * `GET .../turns/current` attaches a covering snapshot document today —
   * both always call `build_details_dto` with `snapshot_doc=None` (see
   * `services/chat_turn_service.py`), so this is always absent on those two
   * responses in practice — but declaring it here (rather than only on
   * `DurableChatTurnStatusResponse`) lets every caller of `build_details_dto`
   * read the SAME honest authoritative-vs-synthesized signal instead of each
   * guessing from emptiness (issue #2513 finding 2).
   */
  replayGap?: DurableChatTurnReplayGap | null
}

export type DurableChatTurnAcceptedResponse = DurableChatTurnDetailsResponse & {
  /** True when idempotent acceptance attached to an already-existing turn. */
  attached: boolean
}

export type DurableChatTurnCurrentResponse = DurableChatTurnDetailsResponse

export type DurableChatTurnStatusResponse = DurableChatTurnDetailsResponse & {
  events: DurableChatTurnEvent[]
  terminal: boolean
  replayGap: DurableChatTurnReplayGap | null
}

export type DurableChatTurnCancelRequest = {
  turnId: string
  reason?: string
}

export type DurableChatTurnCancelResponse = {
  sessionId: string
  turnId: string
  epoch: number
  status: DurableChatTurnStatus
  cancelRequestedAt: string
  cancelReason?: string
}

export type DurableChatTurnControlRequest = {
  turnId: string
  clientControlId: string
  action: "tool_decision"
  requestId: string
  callId: string
  approved: boolean
  reason?: string
}

export type DurableChatTurnControlResponse = {
  sessionId: string
  turnId: string
  epoch: number
  status: DurableChatTurnStatus
  clientControlId: string
  accepted: boolean
}

/** One mid-run steer note posted to a durable turn (#2814).
 *
 *  `seq` is a 1-based per-turn sequence the server derives the control id
 *  from, so two tabs racing on the same value collide on the id and the second
 *  is refused rather than silently sharing a slot. `steerId` is the client's
 *  own chip id, echoed back on the `steer_status` event that settles it. */
export type DurableChatTurnSteerRequest = {
  turnId: string
  seq: number
  content: string
  steerId: string
}

export type DurableChatTurnSteerResponse = {
  sessionId: string
  turnId: string
  epoch: number
  status: DurableChatTurnStatus
  clientControlId: string
  seq: number
  accepted: boolean
}

export type LibraryCategory = {
  value: string
  count: number
}

export type RouteOverrides = {
  // Feature toggles
  disableWebSearch?: boolean
  disableWorkflows?: boolean
  disablePrompts?: boolean
  disableAgent?: boolean
  disableHighReasoning?: boolean
  disableServiceDesk?: boolean
  disableVoiceMode?: boolean
  disableM365?: boolean
  disableNetbox?: boolean
  disableXOne?: boolean
  // Hides the Plugins submenu and strips the plugin-store tools on this route.
  disablePlugins?: boolean
  disableTrainingSearch?: boolean
  disableDocumentSearch?: boolean
  // Hides the Deep Explorer mode toggle and forces plain Explorer for this
  // route. Locked-down routes use this to keep users on the single-pass
  // approach; the backend deep WS route also rejects when this is set.
  disableDeep?: boolean
  // Forces the plain chat home (logo + greeting + example questions) instead of
  // the personalized "home_context" layout. Lets locked-down routes opt out of
  // the M365/Hello-AI enriched home and its consent prompt.
  disablePersonalizedHome?: boolean

  // Model and effort preselected on this route when the user has made no
  // pick (#3327). Users can still change both in the model picker.
  defaultModelId?: string
  defaultReasoningEffort?: string

  // Library restrictions
  restrictToLibraryId?: string
  restrictToLibraryIds?: string[]
  enableCategoryFiltering?: boolean
  showCategoryPicker?: boolean

  // Search configuration
  search_service?: string
  overrideSessionType?: string

  // Chat path override
  chatPath?: string

  // CV library auto-detection keyword (defaults to "cv" when absent)
  cvLibraryKeyword?: string

  // Future extensibility
  [key: string]: unknown
}

export type Route = {
  id: string
  name: string
  path: string
  component: string
  enabled: boolean
  default_route?: boolean
  header: string
  /** Optional kebab-case lucide icon name (e.g. "message-circle") shown in the
   *  module gallery. Falls back to a per-route default when unset. */
  icon?: string
  navigation_text: string | { [key: string]: string }
  info_text: string | { [key: string]: string }
  example_questions:
    | { text: string; value: string }[]
    | { [key: string]: { text: string; value: string }[] }
  modules?: string[]
  element?: ReactElement
  overrides?: RouteOverrides
}

export type Feature = {
  id: string
  name: string
  description: string
  enabled: boolean
  config?: Record<string, unknown> & {
    enabled_modes?: DurableChatTurnMode[]
  }
  requires?: {
    groups?: string[]
    oids?: string[]
  }
  block?: {
    groups?: string[]
    oids?: string[]
  }
}

export type TrainingGating = {
  enabled: boolean
  ai_awareness_completed: boolean
  has_legacy_training: boolean
}

export type ReasoningPresetConfig = {
  model_id: string
  effort: string | null
}

export type Config = {
  memoryBackend?: "prism"
  docxTemplateEnabled?: boolean
  applicationVersion: string
  deploymentSlot?: string | null
  showGPT4VOptions: boolean
  models: GPTModelItem[]
  indexes: IndexModel[]
  showCustomGPTModelOptions: boolean
  showWebSearchOption: boolean
  showM365Option: boolean
  showXOneOption?: boolean
  /** The stamp has the plugin store deployed. Tenant enablement and the
   *  user's own installations come from /api/plugins/catalog. */
  showPluginsOption?: boolean
  /** Hosted agents are offered on this deployment (backend
   *  `HOSTED_AGENTS_ENABLED`, off by default and on only for on-prem
   *  customers). Missing means off: an older backend never serves them. */
  hostedAgentsEnabled?: boolean
  groupSearchEnabled?: boolean
  max_token_limits: number
  customer: string
  features?: Feature[]
  /**
   * Explorer slash/pill reasoning presets (model + effort). Always present
   * from `/api/config` — independent of the explorer_fast_router feature flag.
   */
  reasoning_presets?: Record<string, ReasoningPresetConfig>
  frontend: {
    routes: Route[]
    training_modules: Array<{
      enabled: boolean
      [key: string]: unknown
    }>
  }
  user_policy: {
    /** Platform admin (the admin_dashboard group): full admin dashboard access. */
    is_platform_admin: boolean
    /** Customer admin: sees the admin dashboard filtered by delegated capabilities. */
    is_customer_admin: boolean
    /**
     * Capability key -> delegation mode for customer admins. Empty until the
     * per-customer delegation matrix ships (admin split epic).
     */
    admin_capabilities: Record<string, string>
    is_prompt_admin: boolean
    is_workflow_admin: boolean
    is_source_admin: boolean
    is_parser_admin: boolean
    is_usecase_admin: boolean
    is_tender_admin: boolean
  }
  /** Who may create/share flows: "everyone" (default) or "restricted" (workflow admins only). */
  flowCreationPolicy?: "everyone" | "restricted"
  /** True when the dedicated Flow email sender is configured and ready to send. */
  flowEmailReady?: boolean
  adminUseRealData?: boolean
  enabled_tools?: string[]
  // When true, file types are restricted to Azure OpenAI supported formats
  useResponsesFileSearch?: boolean
  /** Docx/xlsx previews slower than this (ms) may trigger auto gating; from config (DOCUMENT_PREVIEW_SLOW_MS). */
  documentPreviewSlowMs?: number
  training_gating?: TrainingGating
  /**
   * Local AI Lead feature configuration. The `group_attribute` field controls
   * which Entra user attribute ("department" by default) is used for the
   * filter chip and quick-add CTA in the Local AI Lead admin UI.
   */
  /**
   * Plan entitlements for the signed-in user (fair use round 2, #3087).
   * Always sent by a current backend, `{enabled:false, plan:null,
   * entitlements:null}` when fair use is off — optional here so older
   * backends and fixtures still type-check. `models[]` above is already
   * filtered by plan and its efforts capped at `max_reasoning_effort`, so the
   * picker needs no second filter; this block is what the Deep/voice locks
   * and the budget popover's plan line read.
   */
  fair_use?: ConfigFairUse
  local_ai_lead_config?: {
    group_attribute: "department" | "companyName" | "officeLocation" | "city"
    /** Azure AD group OID used to scope directory searches to platform users. */
    user_group_id?: string | null
    /** Display name of the scoping group (stored alongside ID for UI display). */
    user_group_name?: string | null
  }
}

export type Prompt = {
  prompt_key: string
  display_name: string
}

export type PromptLibraryItem = {
  id: string
  title: string
  content: string
  type: "personal" | "company"
  created_at?: string
  updated_at?: string
  defaultWebSearch?: boolean
  defaultModelId?: string | null
  defaultHighReasoning?: boolean
  useTrustedSources?: boolean
  trustedSourceUrls?: string[]
  trustedMode?: "only" | "preferred"
  files?: { filename: string; uploaded_at?: string }[]
  enabledIndexIds?: string[]
}

export type PromptLibraryResponse = {
  prompts: PromptLibraryItem[]
}

export type UpsertPromptRequest = {
  id?: string
  title: string
  content: string
  type: "personal" | "company"
  defaultWebSearch?: boolean
  defaultModelId?: string | null
  defaultHighReasoning?: boolean
  useTrustedSources?: boolean
  trustedSourceUrls?: string[]
  trustedMode?: "only" | "preferred"
  files?: { filename: string; uploaded_at?: string }[]
  enabledIndexIds?: string[]
}

export type FlowPromptItem = {
  id: string
  title: string
  content: string
  type: "flow_personal" | "flow_company"
  created_at?: string
  updated_at?: string
}

export type FlowPromptResponse = {
  prompts: FlowPromptItem[]
}

export type UpsertFlowPromptRequest = {
  id?: string
  title: string
  content: string
  type: "flow_personal" | "flow_company"
}

export type TrustedSourceItem = {
  id: string
  name: string
  url: string
  type: "personal" | "company"
  created_at?: string
  updated_at?: string
}

export type TrustedSourceResponse = {
  sources: TrustedSourceItem[]
}

export type UpsertTrustedSourceRequest = {
  id?: string
  name: string
  url: string
  type: "personal" | "company"
}

export type WorkflowChatMessageRole = "user" | "assistant" | "system"

export type WorkflowChatMessage = {
  id: string
  role: WorkflowChatMessageRole
  content: string
  created_at: string
}

export type WorkflowItem = {
  id: string
  title: string
  content: string | object // Allow object for graph data
  file_required: boolean
  content_revision?: number
  created_at?: string
  updated_at?: string
  // Workflow landing page fields
  visibility: "company" | "personal" | "shared"
  category?: string
  tags?: string[]
  last_run?: string
  runs_this_week?: number
  owner?: string
  owner_name?: string // Display name of the owner
  is_favorite?: boolean
  description?: string
  chat_history?: WorkflowChatMessage[]
  // Project context - default project to run workflow against
  default_project_id?: string
  // Sharing (Fas 1b): participants this flow is shared with (owner-side display).
  participants?: { type: "user" | "group"; id: string; name?: string; role: "runner" | "editor" }[]
  // Present on flows surfaced via the shared list: the viewer's granted role.
  shared_role?: "runner" | "editor"
}

export type WorkflowResponse = {
  workflows: WorkflowItem[]
}

export type UpsertWorkflowRequest = {
  id?: string
  title: string
  content: string | object // Allow object for graph data
  file_required: boolean
  // Optional workflow metadata
  visibility?: "company" | "personal"
  category?: string
  tags?: string[]
  description?: string
  // Project context - default project to run workflow against
  default_project_id?: string
}

export type UpdateWorkflowChatRequest = {
  chat_history: WorkflowChatMessage[]
}

export type ChatMessageModel = {
  id: string
  chatId: string
  content: string
  created: string
  updated: string
  version: string
}

// Participant types for shared sessions
export type ChatSessionParticipantRole = "owner" | "reader"

export type MessageVisibilityMode = "full_history" | "partial_history" | "specific_message"

export type ChatSessionParticipant = {
  oid: string
  displayName: string
  type: "user" | "group"
  role: ChatSessionParticipantRole
  messageAccessMode: MessageVisibilityMode
  sharedFromMessageId?: string // Set when mode is partial_history or specific_message
  sharedAt?: string // ISO timestamp
  isOriginalOwner?: boolean
  // Set on the previous owner's participant entry when ownership was transferred
  // away from them; lets the new owner's sidebar mark a "made you owner" chat.
  isFormerOwner?: boolean
}

export type ChatSessionParticipantsResponse = {
  participants: ChatSessionParticipant[]
  currentUserRole: ChatSessionParticipantRole
  ownerUserId: string
}

export type ChatSessionModel =
  | Partial<{
      memoryOffTransferred: boolean
      id: string
      userId: string
      title: string
      sessionType: string
      created: string
      updated: string
      version: string
      sensitiveData: boolean
      projectId?: string
      // New metadata used by chat list UI
      lastFunctionUsed?:
        | "chat"
        | "prompt_template"
        | "flow"
        | "agent"
        | "web_search"
        | "microsoft_365_search"
        | "voice"
      iconType?:
        | "chat"
        | "prompt_template"
        | "flow"
        | "agent"
        | "web_search"
        | "microsoft_365_search"
        | "voice"
      // File references uploaded to this session (persisted)
      file_refs?: string[]
      // Sharing - participants for shared sessions
      participants?: ChatSessionParticipant[]
      currentUserRole?: ChatSessionParticipantRole
      // Notification status
      hasUnviewedNotification?: boolean
      invitationNotification?: {
        invitedBy: string
        invitedByDisplayName: string
        role: ChatSessionParticipantRole
        messageAccessMode: MessageVisibilityMode
      }
      // Breakout lineage tracking
      sourceSessionId?: string
      sourceMessageId?: string
      // Personal search (owner only): the per-chat switch, and whether personal content is in the chat
      personalSearch?: { enabled: boolean; updatedAt?: string | null }
      personalTainted?: boolean
      hostedAgentId?: string
      // Dynamic title tracking
      title_is_generic?: boolean
      user_set_title?: boolean
      pending_title_suggestion?: {
        suggested_title: string
        reason: string
        confidence: number
        theme?: string
      }
    }>
  | null
  | undefined

/**
 * Response from `GET /api/explorer-deep/state`.
 *
 * `had_state` is false when the checkpointer has no record for the session's
 * thread or when the record has no todos/files yet. Callers hydrate the Deep
 * Explorer store only when `had_state` is true.
 *
 * `live_*` fields carry an in-flight snapshot written periodically by the
 * Deep Explorer streaming loop (see `_flush_live_snapshot` in
 * explorer_deep.py). They let a second tab opening the session see
 * activity / sources without subscribing to the WebSocket the originating
 * tab owns. Cleared once the run reaches a terminal status — at that point
 * the saved assistant message context is the canonical store.
 */
export type DeepStateResponse = {
  had_state: boolean
  request_generation_id?: string | null
  agent_run_request_generation_id?: string | null
  checkpoint_request_generation_id?: string | null
  live_request_generation_id?: string | null
  todos: Array<{
    id: string
    content?: string
    text?: string
    status: "pending" | "in_progress" | "done"
    started_at?: string | null
    completed_at?: string | null
  }>
  files: Record<string, string | { content?: unknown; [k: string]: unknown }>
  file_prev_content: Record<string, string | { content?: unknown; [k: string]: unknown }>
  snapshot_seq: number
  token_budget: { used: number; total: number } | null
  live_activity_events?: Array<Record<string, unknown>>
  live_data_points?: Record<string, unknown>
  live_documents?: unknown[]
  live_artifacts?: Array<Record<string, unknown>>
  live_updated_at?: string | null
  /** True when the in-process run broadcaster has an active slot for
   *  this session (streaming task still alive). Lets the frontend
   *  trigger a reattach WS for pre-approval runs that don't yet have
   *  an agent_run row. */
  broadcaster_active?: boolean
  /** True when the current turn's plan has already been approved. Lets
   *  a second tab opening mid-run skip the approval UI even if the
   *  broadcaster replays a buffered ``deep_plan_proposed`` frame. */
  plan_approved?: boolean
}

export type IndexModel = {
  id: string
  blocked_modules?: string[] // Routes that are blocked from accessing this library
  display_name: string
  description: string
  default_enabled: boolean
  lastUsedAt?: number
  /** When true, citation preview prefers the pre-conversion blob (Cosmos library definition). */
  use_unmodified_previews?: boolean
}

// WebSocketClass
export interface FileData {
  filename: string
  content: string // Base64 encoded string
}

export interface InitialMessage {
  idToken?: string
  request: ChatAppRequest
  files?: FileData[]
}

export interface HeartbeatMessage {
  type: "heartbeat"
  timestamp: number
}

export interface DoneMessage {
  type: "done"
  data: ChatAppResponse
}

export interface ErrorMessage {
  type: "error"
  message: string
  status?: number
  // Set by backend typed error chunks (e.g. the stream-stall watchdog):
  // a per-request reference id and a machine-readable code.
  error_id?: string
  data?: {
    message?: string
    code?: string
    error_id?: string
    // Deep Explorer puts its i18n hint here (e.g. the token runaway guard).
    translation_key?: string
    translation_params?: Record<string, unknown>
  }
  // Optional i18n hint set by FileUploadError-backed errors (e.g. an
  // unreadable/encrypted file attachment): a full i18next key path plus
  // interpolation values. When present, prefer translating this over
  // showing `message` (English) verbatim.
  translation_key?: string
  translation_params?: Record<string, unknown>
}

export interface ChunkMessage {
  type: "chunk"
  data: {
    content?: string
    tool_calls?: unknown[]
    finish_reason?: string
    response_complete?: boolean
    context?: unknown
  }
}

type GroupTitleDetails = {
  translation_key: string
  depth?: number
}

export type GraphEvidenceCard = {
  id?: string
  title?: string
  snippet?: string
  source?: string
  score?: number
}

type GraphEventBase = {
  type:
    | "enter_node"
    | "evaluate_children"
    | "choose_child"
    | "retrieve_leaf_text"
    | "merge_results"
    | "rerank_results"
    | "final_context_ready"
    | "tree_failed"
  node_id?: string
}

export type EnterNodeEvent = GraphEventBase & {
  type: "enter_node"
  parent_id?: string | null
  label?: string
  depth?: number
}

export type EvaluateChildrenEvent = GraphEventBase & {
  type: "evaluate_children"
  child_ids?: string[]
  child_count?: number
}

export type ChooseChildEvent = GraphEventBase & {
  type: "choose_child"
  child_id?: string
  reason?: string
  score?: number
  evidence?: GraphEvidenceCard[]
}

export type RetrieveLeafTextEvent = GraphEventBase & {
  type: "retrieve_leaf_text"
  snippet?: string
  source?: string
  evidence?: GraphEvidenceCard[]
}

export type MergeResultsEvent = GraphEventBase & {
  type: "merge_results"
  merged_count?: number
  child_ids?: string[]
}

export type RerankResultsEvent = GraphEventBase & {
  type: "rerank_results"
  rankings?: GraphEvidenceCard[]
}

export type FinalContextReadyEvent = GraphEventBase & {
  type: "final_context_ready"
  evidence?: GraphEvidenceCard[]
}

export type TreeFailedEvent = GraphEventBase & {
  type: "tree_failed"
  error?: string
  reason_code?: string
  details?: Record<string, unknown>
}

export type ThoroughSearchGraphEvent =
  | EnterNodeEvent
  | EvaluateChildrenEvent
  | ChooseChildEvent
  | RetrieveLeafTextEvent
  | MergeResultsEvent
  | RerankResultsEvent
  | FinalContextReadyEvent
  | TreeFailedEvent

type Details = {
  group_title?: string | GroupTitleDetails
  original_details?: string
  message?: string
  document_reference?: DocumentReferencePreview
  document_references?: DocumentReferencePreview[]
  event?: ThoroughSearchGraphEvent
  agent?: {
    name?: string
    role?: string
    type?: string
    isLoading?: boolean
  }
  [key: string]: unknown
}

export interface ActivityMessage {
  type: "activity"
  activity_type: string
  message: string
  details?: Details
  timestamp: number
}

export type ThoroughSearchGraphDetails = Details & {
  event?: ThoroughSearchGraphEvent
  traversal_mode?: "real" | "fallback"
}

export interface ThoroughSearchGraphActivityMessage extends ActivityMessage {
  activity_type: "thorough_search_graph"
  details?: ThoroughSearchGraphDetails
}

export interface QueueStatusMessage {
  type: "queue_status"
  request_id: string
  status: string
  position: number
  estimated_wait_time: number
  queue_size: number
  timestamp: number
}

export interface QueuedMessage {
  type: "queued"
  request_id: string
  message: string
  timestamp: number
}

export interface ProcessingStartedMessage {
  type: "processing_started"
  request_id: string
  message: string
  timestamp: number
}

export interface MessageSavedMessage {
  type: "message_saved"
  data: {
    session_id: string | null
    question?: string
    response: ChatAppResponse
  }
}

export interface TitleStateChangedMessage {
  type: "title_state_changed"
  data: {
    session_id: string | null
    title_updated?: { new_title: string; auto_accepted?: boolean }
    /**
     * Whether the new title still counts as generic (eligible for
     * dynamic-title improvement). Sent on early titles (provisional
     * truncated question, parallel fast-LLM title) and on dynamic-eval
     * auto-accepts. The client defaults to false when the flag is absent
     * (frames predating it).
     */
    title_is_generic?: boolean
    pending_title_suggestion?: {
      suggested_title: string
      reason: string
      confidence: number
      theme?: string
    } | null
  }
}

export interface FollowupQuestionsMessage {
  type: "followup_questions"
  data: { followup_questions: string[]; message_id?: string | null; messageId?: string | null }
}

// ── Deep Explorer frame types ────────────────────────────────────────────────

export interface DeepTodoFrame {
  id: string
  text: string
  status: "pending" | "in_progress" | "done"
  started_at?: string | null
  completed_at?: string | null
}

export interface DeepFileFrame {
  path: string
  content: string
  previous_content?: string | null
  lastModified: number
}

export interface DeepStateSnapshotMessage {
  type: "deep_state_snapshot"
  data: {
    seq: number
    todos?: DeepTodoFrame[]
    files?: DeepFileFrame[]
    token_budget?: { used: number; total: number } | null
  }
}

export interface DeepPlanProposedMessage {
  type: "deep_plan_proposed"
  data: {
    todos: DeepTodoFrame[]
  }
}

/**
 * A Deep tool with an approval policy was called after plan approval; the
 * backend waits for a `deep_tool_approval_decision` frame on the same Deep
 * socket (#2962). See features/ai-explorer/utils/deep-tool-approvals.ts.
 */
export interface DeepToolApprovalRequiredMessage {
  type: "deep_tool_approval_required"
  /** Per-connection frame sequence; used to register a frame once when the
   *  Deep socket delivers it to more than one subscriber. */
  _seq?: number
  data: {
    tool: string
    args: Record<string, unknown>
    summary?: string
    timeout_secs?: number
  }
}

export interface DeepAskUserMessage {
  type: "deep_ask_user"
  data: {
    ask_id?: string
    context: string
    questions: Array<{
      id: string
      question: string
      type: "text" | "single_choice" | "multi_choice"
      options?: string[]
      default?: string
    }>
    proposed_todos?: DeepTodoFrame[]
  }
}

/**
 * Pre-approval reasoning chunk from Deep Explorer. The backend emits this
 * instead of `chunk` while the plan has not yet been approved, so the frontend
 * can route the planner's prose to the ThinkingPanel instead of the answer
 * bubble. See app/backend/approaches/explorerdeep/streaming.py.
 */
export interface DeepPlanningChunkMessage {
  type: "deep_planning_chunk"
  data: { content: string }
}

/**
 * B7: live token chunks emitted by a dispatched subagent's chat-model stream.
 * Each frame carries the dispatch id so the frontend can attribute the chunk
 * to the right WorkerLane and show the subagent's reasoning as it forms,
 * instead of waiting for the parent ``task`` tool_end flush of `output_text`.
 */
export interface DeepSubagentChunkMessage {
  type: "deep_subagent_chunk"
  data: { dispatch_id: string; source: string; delta: string }
}

/**
 * B4: live orchestrator reasoning between tool calls. Emitted optimistically
 * for every post-approval orchestrator chat-model token; finalised by either
 * a ``_complete`` (the message had tool_calls — chunks were inter-tool
 * reasoning, keep them) or a ``_discard`` (no tool_calls — chunks were the
 * answer prefix, purge them). ``run_id`` groups chunks belonging to the
 * same chat-model invocation into a single block.
 */
export interface DeepOrchestratorReasoningChunkMessage {
  type: "deep_orchestrator_reasoning_chunk"
  data: { run_id: string; delta: string }
}

export interface DeepOrchestratorReasoningCompleteMessage {
  type: "deep_orchestrator_reasoning_complete"
  data: {
    run_id: string
    /** Reserved for future use. The AIMessage tool_call id and the activity
     *  event id live in different namespaces, so the frontend orders blocks
     *  by arrival timestamp instead of pairing on this id. */
    following_tool_id: string | null
  }
}

export interface DeepOrchestratorReasoningDiscardMessage {
  type: "deep_orchestrator_reasoning_discard"
  data: { run_id: string }
}

/**
 * Round-4: subagent variant of the orchestrator reasoning frames. Same
 * machinery (chunk → optimistic emit, complete/discard → keep or purge),
 * but addressed to a specific subagent dispatch so the subagent's lane
 * shows reasoning bubbles interleaved with its own tool calls.
 */
export interface DeepSubagentReasoningChunkMessage {
  type: "deep_subagent_reasoning_chunk"
  data: { dispatch_id: string; run_id: string; delta: string }
}

export interface DeepSubagentReasoningCompleteMessage {
  type: "deep_subagent_reasoning_complete"
  data: { dispatch_id: string; run_id: string }
}

export interface DeepSubagentReasoningDiscardMessage {
  type: "deep_subagent_reasoning_discard"
  data: { dispatch_id: string; run_id: string }
}

/**
 * Emitted by the backend after plan approval, once it extracts the chosen
 * deliverable format (e.g. "research_report", "memo") from the planner's
 * `<!-- deliverable_type: ... -->` comment in /plan.md. The frontend uses
 * it to style the /deliverable.md render with the right badge + chrome.
 *
 * Also carries the resolved ``execution_mode`` so the UI can surface the
 * post-approval bound the gate middleware will enforce. Older backends
 * may omit the field — callers must treat it as optional.
 */
export interface DeepDeliverableTypeMessage {
  type: "deep_deliverable_type"
  data: { deliverable_type: string; execution_mode?: string }
}

/**
 * Smart follow-up suggestion with an attached mini-plan. Emitted at the
 * end of a Deep Explorer turn — the executor wrote /followups.json which
 * the backend parses and forwards as this frame.
 */
export interface DeepFollowupSuggestion {
  question: string
  mini_plan: string[]
}

export interface DeepFollowupSuggestionsMessage {
  type: "deep_followup_suggestions"
  data: { suggestions: DeepFollowupSuggestion[] }
}

/**
 * Fact-checker verdicts emitted once `/verifications.json` is written by
 * the fact-checker subagent at the end of a Deep Explorer turn. Used to
 * decorate citations in the deliverable with green/yellow badges.
 */
export interface DeepCitationVerification {
  /** Raw string inside `[[...]]` — the frontend matches this against
   *  rendered citations via the existing `data-citation-raw` attribute. */
  citation: string
  /** true = quote matched the source, false = mismatch / source not found,
   *  null = no quote in citation (legacy format, can't verify). */
  verified: boolean | null
  issue?: string
  actual_snippet?: string
}

export interface DeepCitationVerificationMessage {
  type: "deep_citation_verification"
  data: {
    verifications: DeepCitationVerification[]
    total: number
    verified: number
    unverified: number
  }
}

/**
 * Emitted when the backend abandons a pending plan decision (currently only
 * on the server-side wait timeout). The frontend clears `planProposed` so
 * the approval card disappears and the user gets a clear signal that the
 * turn was cancelled server-side. `reason` lets the UI differentiate a
 * user-visible message (timeout vs explicit backend cancel).
 */
export interface DeepPlanCancelledMessage {
  type: "deep_plan_cancelled"
  data: {
    reason: "timeout" | "backend_error"
    message?: string
  }
}

/**
 * Emitted when the Deep Explorer graph is paused because Azure OpenAI
 * returned 429 (TPM/RPM burst). The frontend shows a waiting toast while
 * the backend sleeps and resumes from the last checkpoint. If all auto-retries
 * are exhausted, a standard `error` frame with `retry_after_hint` follows.
 */
export interface DeepRateLimitedMessage {
  type: "deep_rate_limited"
  data: {
    retry_in_s: number
    attempt: number
    max_attempts: number
  }
}

/**
 * Deep Explorer-specific error frame. Shares the top-level `type: "error"`
 * channel with plain chat errors but always carries a structured `data`
 * payload and `source === "deep_explorer"`. When the error comes from an
 * exhausted rate-limit retry loop, `retry_after_hint` hints at how many
 * seconds the user should wait before retrying the turn.
 */
export interface DeepErrorMessage {
  type: "error"
  data: {
    message: string
    source: "deep_explorer"
    retry_after_hint?: number
    // Machine-readable stop reason and i18n hint, e.g.
    // `deep_token_budget_exceeded` / `deepExplorer.token_budget_exceeded`.
    code?: string
    translation_key?: string
    translation_params?: Record<string, unknown>
  }
}

export type ServerMessage =
  | HeartbeatMessage
  | DoneMessage
  | ErrorMessage
  | ChunkMessage
  | ActivityMessage
  | QueueStatusMessage
  | QueuedMessage
  | ProcessingStartedMessage
  | MessageSavedMessage
  | TitleStateChangedMessage
  | FollowupQuestionsMessage
  | DeepStateSnapshotMessage
  | DeepPlanProposedMessage
  | DeepAskUserMessage
  | DeepToolApprovalRequiredMessage
  | DeepPlanningChunkMessage
  | DeepSubagentChunkMessage
  | DeepOrchestratorReasoningChunkMessage
  | DeepOrchestratorReasoningCompleteMessage
  | DeepOrchestratorReasoningDiscardMessage
  | DeepSubagentReasoningChunkMessage
  | DeepSubagentReasoningCompleteMessage
  | DeepSubagentReasoningDiscardMessage
  | DeepDeliverableTypeMessage
  | DeepFollowupSuggestionsMessage
  | DeepCitationVerificationMessage
  | DeepPlanCancelledMessage
  | DeepRateLimitedMessage
  | DeepErrorMessage
  | ArtifactEmitMessage
  | ArtifactUpdateMessage
  | DeepActivityEventMessage
export interface GraphFileItem {
  id: string
  name: string
  webUrl: string
  size: number
  lastModified: string
  driveId: string
  siteId: string
  siteUrl: string
  path: string
  mimeType: string
  isFolder?: boolean
}

export interface GraphListResponse {
  items: GraphFileItem[]
  next_cursor?: string | null
}

// ---------------------------------------------------------------------------
// Workspace artifact contract — Phase 1.1
//
// Mirrors `app/backend/approaches/explorerdeep/artifact_models.py`. Field
// names match the wire format exactly (snake_case). The discriminator is the
// `type` field on each payload variant; the envelope's `type` must equal
// `payload.type` (the backend enforces this on parse).
//
// Spec: .workspace-spec/WORKSPACE_CONTRACT.md
// ---------------------------------------------------------------------------

export type ArtifactType =
  | "document"
  | "short_answer"
  | "email_draft"
  | "table"
  | "list"
  | "docx"
  | "xlsx"
  | "pptx"
  | "pdf"
  | "image"
  | "diagram"
  | "action_receipt"
  | "pending_action"
  | "ask_user"
  | "plan"
  | "unknown"

export type ArtifactStatus = "draft" | "ready" | "pending_approval" | "executed" | "failed"

export interface Citation {
  id: string
  source_id: string
  page?: number | null
  quote?: string | null
}

export interface TraceRef {
  mission_id: string
}

export interface ArtifactAction {
  id: string
  label: string
  icon?: string | null
  primary?: boolean
  requires_confirmation?: boolean
  /**
   * Render the action but make it inactive (countdowns, gating).
   * `ArtifactFrame.ActionButton` must honour this and skip the click
   * handler when true. Mirrors `ArtifactAction.disabled` in
   * `artifact_models.py`. See WORKSPACE_CONTRACT.md § "Actions".
   */
  disabled?: boolean
}

export interface TableColumn {
  key: string
  label: string
  type?: "text" | "number" | "date"
}

export interface OfficeFilePreview {
  kind: "text_excerpt" | "page_image" | "first_n_rows"
  data: string | string[]
}

export interface SheetMeta {
  name: string
  row_count: number
}

export interface SlideMeta {
  index: number
  title: string
  thumbnail_url?: string | null
}

export interface PlanTodo {
  id: string
  text: string
  status: "pending" | "in_progress" | "completed"
}

export type ActionTargetSystem =
  | "m365_outlook"
  | "m365_word"
  | "m365_excel"
  | "m365_powerpoint"
  | "servicedesk"
  | "netbox"
  | "sharepoint"
  | "other"

export interface ActionTarget {
  system: ActionTargetSystem
  label: string
  external_id?: string | null
  external_url?: string | null
}

export type ActionVerb =
  | "sent_email"
  | "created_ticket"
  | "updated_record"
  | "scheduled_meeting"
  | "filed_document"
  | "other"

export type ActionResult = "success" | "partial" | "failed"

export interface DocumentPayload {
  type: "document"
  format?: "markdown"
  body: string
  word_count: number
  estimated_read_minutes: number
}

export interface ShortAnswerPayload {
  type: "short_answer"
  format?: "markdown"
  body: string
}

export interface EmailDraftPayload {
  type: "email_draft"
  subject: string
  body: string
  body_format?: "markdown" | "html"
  to?: string[]
  cc?: string[]
  bcc?: string[]
}

/** Covers both `table` and `list` artifact types (list = subset of table). */
export interface TablePayload {
  type: "table" | "list"
  format: "markdown" | "structured"
  body?: string | null
  columns?: TableColumn[] | null
  rows?: Record<string, unknown>[] | null
}

/** Used by `docx`, `xlsx`, `pptx`, and `pdf` artifact types. */
export interface OfficeFilePayload {
  type: "docx" | "xlsx" | "pptx" | "pdf"
  file_path: string
  file_size_bytes: number
  mime_type: string
  preview?: OfficeFilePreview | null
  sheets?: SheetMeta[] | null
  slides?: SlideMeta[] | null
  page_count?: number | null
  external_url?: string | null
}

export interface ActionReceiptPayload {
  type: "action_receipt"
  verb: ActionVerb
  target: ActionTarget
  result: ActionResult
  detail?: string | null
  rolled_back?: boolean
}

/** Inner shape of `PendingActionPayload.proposed`: the unsent receipt. */
export interface ProposedActionPayload {
  verb: ActionVerb
  target: ActionTarget
  result: ActionResult
  detail?: string | null
  rolled_back?: boolean
}

export interface PendingActionPayload {
  type: "pending_action"
  proposed: ProposedActionPayload
  expires_at?: string | null
}

export interface ImagePayload {
  type: "image"
  url: string
  alt?: string | null
  width?: number | null
  height?: number | null
  prompt?: string | null
}

export interface DiagramPayload {
  type: "diagram"
  format: "mermaid" | "graphviz" | "svg"
  source: string
  rendered_url?: string | null
}

export interface PlanPayload {
  type: "plan"
  understanding: string
  expected_result: string
  sources_to_consult?: string[]
  todos?: PlanTodo[]
  /**
   * Raw markdown of the planner's "## Steps" section, when emitted. The
   * `todos` list is the structured/interactive view; this carries the
   * longer-form per-step rationale that the terse todo text alone doesn't
   * convey. Null when the planner emitted no Steps section.
   */
  steps_markdown?: string | null
  deliverable_format?: string | null
}

/**
 * Phase 2 (workspace-focus): a structured-question prompt rendered as a
 * center-workspace artifact instead of the right-peek modal. Mirrors the
 * `ask_user` tool's `QuestionItem` schema.
 */
export interface AskUserQuestion {
  id: string
  question: string
  type: "text" | "single_choice" | "multi_choice"
  options?: string[] | null
  default?: string | null
}

export interface AskUserPayload {
  type: "ask_user"
  /** Present on newly emitted prompts; optional for persisted pre-correlation artifacts. */
  ask_id?: string | null
  context: string
  questions: AskUserQuestion[]
  proposed_todos?: Record<string, unknown>[] | null
  // Populated post-submit via an `artifact_update` event keyed off question
  // id. Pre-submit / pre-skip it is null and the renderer shows the form;
  // post-submit the read-only summary renders Q+A pairs from this map.
  answers?: Record<string, string | string[]> | null
}

export interface UnknownPayload {
  type: "unknown"
  file_path?: string | null
  mime_type?: string | null
  description?: string | null
}

export type ArtifactPayload =
  | DocumentPayload
  | ShortAnswerPayload
  | EmailDraftPayload
  | TablePayload
  | OfficeFilePayload
  | ActionReceiptPayload
  | PendingActionPayload
  | AskUserPayload
  | ImagePayload
  | DiagramPayload
  | PlanPayload
  | UnknownPayload

export interface Artifact {
  // Identity
  id: string
  version: number
  parent_id?: string | null
  /**
   * Producer hint: when set, the streaming layer (and the frontend store)
   * auto-copies this into `parent_id` if `parent_id` is unset, so the lineage
   * tree gets linked even when the producer only knows which artifact it
   * refines. See `WORKSPACE_CONTRACT.md` § "Versioning rules".
   */
  refines_artifact_id?: string | null
  conversation_id: string
  turn_id: string
  created_at: string

  // Type + payload (payload demuxes by `type`)
  type: ArtifactType
  payload: ArtifactPayload

  // Display
  title: string
  subtitle?: string | null
  preview_text?: string | null

  // Provenance
  citations?: Citation[]
  trace_ref: TraceRef

  // Affordances
  actions?: ArtifactAction[]

  // Versioning hint
  is_latest: boolean

  // Optional status (mostly for action types)
  status?: ArtifactStatus | null
}

/** WS frame: a brand-new artifact landing in the workspace. */
export interface ArtifactEmitMessage {
  type: "artifact_emit"
  data: Artifact
}

/** WS frame: a partial update to an existing artifact (shallow-merge patch). */
export interface ArtifactUpdateMessage {
  type: "artifact_update"
  data: {
    id: string
    patch: Partial<Artifact>
  }
}

/**
 * WS frame: a Deep Explorer activity event (tool_start / tool_end / status).
 * Backend emits these from `streaming.py` via
 * `yield {"type": "deep_activity_event", "data": <event>}`. The wire shape
 * mirrors `DeepActivityEvent` in the store (defined structurally here to
 * avoid a store→models dependency cycle); see
 * `app/frontend/src/stores/slices/deep-explorer-session-state.ts`.
 */
export interface DeepActivityEventMessage {
  type: "deep_activity_event"
  data: {
    id: string
    event: "tool_start" | "tool_end" | "status"
    tool: string
    source: string
    dispatch_id?: string | null
    timestamp: string
    input_summary?: Record<string, unknown>
    result_summary?: Record<string, unknown>
  }
}
